import { useId, useRef, useState } from "react";
import { RemiButton, RemiOTP, useEffect } from "@remi/next-ui";

type MfaChallengeTotpProps = {
  onVerify: (code: string) => void;
  onTryAnotherMethod?: () => void;
  resetError: () => void;
  loading: boolean;
  error: boolean;
  errorMessage?: string;
};

export const MfaChallengeTotp = ({
  onVerify,
  onTryAnotherMethod,
  resetError,
  loading,
  error,
  errorMessage,
}: MfaChallengeTotpProps) => {
  const [code, setCode] = useState("");
  const errorId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleChange = (next: string) => {
    // Clear the error on first new keystroke so RemiOTP's auto-reset
    // (which fires on slot focus when error===true) doesn't swallow input.
    if (error) {
      resetError();
    }
    setCode(next);
    if (next.length === 6 && !loading) {
      onVerify(next);
    }
  };

  const showError = error && Boolean(errorMessage);

  return (
    <div className="flex flex-col gap-6" data-testid="mfa-challenge-totp">
      <div className="flex flex-col gap-1">
        <h2 ref={headingRef} tabIndex={-1} className="font-semibold text-lg text-text-primary">
          Open your authenticator app
        </h2>
        <p className="text-sm text-text-secondary">Enter the 6-digit code from your authenticator app</p>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <RemiOTP
            value={code}
            onChange={handleChange}
            error={error}
            resetValue={() => setCode("")}
            hideSeparator
            disabled={loading}
            slotClassName="h-14 flex-1 first:rounded-l-lg last:rounded-r-lg"
            groupClassName="w-full"
            containerClassName="w-full"
            autoComplete="one-time-code"
            inputMode="numeric"
            aria-label="6-digit code from your authenticator app"
            aria-invalid={error || undefined}
            aria-describedby={showError ? errorId : undefined}
            data-testid="mfa-otp-input"
          />
          {showError && (
            <span
              id={errorId}
              role="alert"
              className="text-[10px] text-text-destructive"
              data-testid="mfa-otp-input-error-message"
            >
              {errorMessage}
            </span>
          )}
        </div>
        {onTryAnotherMethod && (
          <RemiButton
            variant="tertiary"
            size="sm"
            onClick={onTryAnotherMethod}
            className="-ml-2 self-start text-text-secondary"
            data-testid="mfa-try-another-method"
          >
            Try another method
          </RemiButton>
        )}
      </div>
    </div>
  );
};
